import { isDefined } from "@clipboard-health/util-ts";
import { FacilityRoles, FacilityUserPermissions } from "@src/appV2/FacilityUser/types";
import { differenceInMinutes } from "date-fns";

import { type ShiftChargeRateInfo } from "./hooks/useShiftChargeRates";

export const ChargeUpdateKind = {
  // Only offer VC
  VariableCharge: "VariableCharge",
  // Only offer W&S
  WaitAndSave: "WaitAndSave",
  // The user may choose either VC or W&S
  Either: "Either",
  // The data has not yet loaded
  Loading: "Loading",
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type ChargeUpdateKind = (typeof ChargeUpdateKind)[keyof typeof ChargeUpdateKind];

export function getChargeUpdateKind(parameters: {
  waitAndSaveEnabled: boolean | undefined;
  shiftChargeRates: ShiftChargeRateInfo[];
  shift: { start: string } | undefined;
}): ChargeUpdateKind {
  const { waitAndSaveEnabled, shiftChargeRates, shift } = parameters;

  if (!waitAndSaveEnabled) {
    return ChargeUpdateKind.VariableCharge;
  }

  if (!isDefined(shift)) {
    return ChargeUpdateKind.Loading;
  }

  const { start } = shift;

  const hasManualRateOverride = shiftChargeRates.some((rate) =>
    isDefined(rate.manualRateInMinorUnits)
  );

  if (hasManualRateOverride) {
    return ChargeUpdateKind.VariableCharge;
  }

  const activeWaitAndSaveExists = shiftChargeRates.some((info) =>
    isDefined(info.waitAndSaveAuction)
  );

  if (activeWaitAndSaveExists) {
    return ChargeUpdateKind.WaitAndSave;
  }

  if (!isDefined(start) || differenceInMinutes(new Date(start), new Date()) <= 60) {
    // If the shift starts in less than 1 hour, Smart pricing will price it at
    // the maximum charge rate anyway, so it doesn't make sense to use W&S and
    // we just go for a flat rate.
    return ChargeUpdateKind.VariableCharge;
  }

  return ChargeUpdateKind.Either;
}

interface User {
  access?: string[];
  permissions?: string[];
}

export function canUserEditShiftRates(user: User | undefined, isAdmin: boolean) {
  const userHasAdminRole = user?.access?.includes(FacilityRoles.ADMIN) ?? false;
  const userHasEditShiftRatePermission =
    user?.permissions?.includes(FacilityUserPermissions.EDIT_SHIFT_RATE) ?? false;
  return isAdmin || userHasAdminRole || userHasEditShiftRatePermission;
}

export function getSetPricingSettingsRequestData(parameters: {
  modifiedChargeRates: Record<string, number>;
  shiftChargeRates: ShiftChargeRateInfo[];
  description: string;
}) {
  const { modifiedChargeRates, shiftChargeRates, description } = parameters;

  return Object.entries(modifiedChargeRates).map(([qualification, amountInMinorUnits]) => {
    const chargeRateInfo = shiftChargeRates.find((info) => info.qualification === qualification);
    const defaultRate = chargeRateInfo?.constraints.minimumAmountInMinorUnits;

    const waitAndSaveAuction =
      amountInMinorUnits === defaultRate
        ? null
        : {
            maximumChargeInMinorUnits: amountInMinorUnits,
            startAt: chargeRateInfo?.waitAndSaveAuction?.startAt ?? new Date().toISOString(),
          };

    return {
      qualification,
      description,
      existingAuctionSettingsId: chargeRateInfo?.existingPricingSettingsId,
      waitAndSaveAuction,
      manualChargeInMinorUnits: undefined,
    };
  });
}

export function getManualChargePricingSettingsRequestData(parameters: {
  modifiedChargeRates: Record<string, number>;
  shiftChargeRates: ShiftChargeRateInfo[];
  description: string;
  clearWaitAndSave?: boolean;
}) {
  const { modifiedChargeRates, shiftChargeRates, description, clearWaitAndSave } = parameters;

  return Object.entries(modifiedChargeRates).map(([qualification, amountInMinorUnits]) => {
    const chargeRateInfo = shiftChargeRates.find((info) => info.qualification === qualification);
    const defaultRate = chargeRateInfo?.defaultRateInMinorUnits;

    const manualChargeInMinorUnits = amountInMinorUnits === defaultRate ? null : amountInMinorUnits;

    return {
      qualification,
      description,
      existingAuctionSettingsId: chargeRateInfo?.existingPricingSettingsId,
      waitAndSaveAuction: clearWaitAndSave === true ? null : undefined,
      manualChargeInMinorUnits,
    };
  });
}

export function getPricingSettingsId(shiftId: string, qualification: string) {
  return `${shiftId}:${qualification}`;
}

export function moneyMinorUnitsToMajorUnits(minorUnits: number): number {
  return minorUnits / 100;
}

export function moneyMajorUnitsToMinorUnits(dollars: number): number {
  return Math.round(dollars * 100);
}
